/**
 * assets/search-modal.js
 * ------------------------------------------------------------
 * Điều khiển search modal:
 *  1. Lazy-load filter list (Color / Last / Size...) bằng Section
 *     Rendering API, fetch từ 1 collection cố định (vd /collections/shoes)
 *     -> lấy được filter dù đang ở page nào (home, /about/, product...).
 *  2. Khi bấm "Search": gộp keyword (input[name="q"]) + các checkbox
 *     filter đã check thành query string, redirect sang /search.
 *  3. Khi bấm "Clear": reset input + bỏ check hết checkbox.
 *
 * Modal mở/đóng vẫn do JS hiện có của theme (Dawn) xử lý qua class
 * is-active / js-btn-search. Chỗ nào toggle modal mở, cần dispatch
 * thêm 1 custom event để trigger lazy-load lần đầu:
 *
 *   document.dispatchEvent(new CustomEvent('search-modal:open'));
 */

class SearchModal {
  constructor(root) {
    this.root = root;
    this.filterContainer = root.querySelector("[data-search-filter-container]");
    this.form = root.querySelector("[data-search-modal-form]");
    this.input = this.form ? this.form.querySelector('input[name="q"]') : null;

    this.filtersLoaded = false;
    this.sourceUrl = root.dataset.filterSourceUrl || "/collections/shoes";
    this.sectionId = root.dataset.filterSectionId || "search-modal-facets";

    if (!this.filterContainer || !this.form || !this.input) return;

    this.bindOpenTrigger();
    this.resetKeyword();
    // this.bindFormSubmit();
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => this.loadFilters());
    } else {
      setTimeout(() => this.loadFilters(), 200);
    }
  }

  // Lazy-load filter when the modal is first opened
  // document.dispatchEvent(new CustomEvent("search-modal:open"));
  // Also reset the keyword input every time the modal opens, so it doesn't
  // keep showing the previous search term (e.g. "1440" from
  // `{{ search.terms }}` on the /search page) when the modal is reopened.
  bindOpenTrigger() {
    document.addEventListener("search-modal:open", () => {
      this.resetKeyword();
      if (!this.filtersLoaded) this.loadFilters();
    });
  }

  // Clear the keyword field only (checkboxes are left alone here), so the
  // input always starts blank on open — same idea as the filter Clear
  // button, just triggered automatically instead of on click.
  resetKeyword() {
    if (this.input) this.input.value = "";
  }

  async loadFilters() {
    this.filterContainer.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(
        `${this.sourceUrl}?section_id=${this.sectionId}`,
      );
      if (!response.ok)
        throw new Error(`Fetch filters failed: ${response.status}`);

      const html = await response.text();
      this.filterContainer.innerHTML = html;
      this.filtersLoaded = true;

      this.bindFilterActions();
    } catch (error) {
      console.error("[search-modal] Không load được filter:", error);
      this.filterContainer.innerHTML =
        '<p class="c-search_filters_error">Không tải được bộ lọc, thử lại sau.</p>';
    } finally {
      this.filterContainer.removeAttribute("aria-busy");
    }
  }

  // Bind the Search/Clear button inside the injected HTML.
  bindFilterActions() {
    const submitBtn = this.filterContainer.querySelector(
      "[data-search-modal-submit]",
    );
    const clearBtn = this.filterContainer.querySelector(
      "[data-search-modal-clear]",
    );

    if (submitBtn) submitBtn.addEventListener("click", () => this.submit());
    if (clearBtn) clearBtn.addEventListener("click", () => this.clear());

    // Accordion toggle for each filter group (Color / Last / Size...) on mobile.
    // Must be bound here (after the HTML filter has been injected) and not in
    // outside the document when loading the page, because the filterContainer is empty at that time.
    // (HTML filter fetched async later).
    this.bindAccordion();
  }

  // Toggle collapse, scope in filterContainer to avoid conflicts with other
  // .js-collapse/.js-panel files outside the modal (if the theme uses the same class).
  bindAccordion() {
    const searchAccordions = this.filterContainer.querySelectorAll("[data-search-collapse]");
    const searchPanels = this.filterContainer.querySelectorAll("[data-search-panel]");

    searchAccordions.forEach((trigger, i) => {
      const panel = searchPanels[i];
      if (!panel) return;

      trigger.addEventListener("click", () => {
        trigger.classList.toggle("open");

        if (panel.style.maxHeight) {
          panel.style.maxHeight = null;
        } else {
          panel.style.maxHeight = `${panel.scrollHeight}px`;
        }
      });
    });
  }

  // Pressing Enter in the keyword input box also submits the application.
  bindFormSubmit() {
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.submit();
    });
  }

  submit() {
    const params = new URLSearchParams();
    const keyword = this.input.value.trim();

    params.set("q", keyword || "*");
    params.set("type", "product");
    params.set("options[prefix]", "last");

    this.filterContainer
      .querySelectorAll('input[type="checkbox"]:checked')
      .forEach((checkbox) => {
        params.append(checkbox.name, checkbox.value);
      });

    const searchUrl = (window.routes && window.routes.search_url) || "/search";
    window.location.href = `${searchUrl}?${params.toString()}`;
  }

  clear() {
    this.input.value = "";
    this.filterContainer
      .querySelectorAll('input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.checked = false;
      });
  }
}

document
  .querySelectorAll("[data-search-modal]")
  .forEach((root) => new SearchModal(root));
